'use client'

import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { CheckIcon } from '@heroicons/react/24/solid'
import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { BookingContext } from '@tebuto/widget/booking/context'
import { useContext } from 'react'

export default function BookingSuccess() {
    const { t } = useComponentTranslation()
    const { isClientAuthenticated, bookingResult } = useContext(BookingContext)

    const { title, description, warning, testId } = resolveSuccessCopy({
        t,
        isClientAuthenticated: !!isClientAuthenticated,
        isConfirmed: bookingResult?.isConfirmed ?? false,
        hasDirectBookingResult: Boolean(bookingResult?.ics)
    })

    return (
        <div className="flex justify-center items-center py-8 px-4 min-h-96">
            <div className="w-full max-w-md space-y-4">
                <div className="flex flex-col items-center text-center gap-5 py-4">
                    <div className="relative flex items-center justify-center">
                        <div className="absolute w-20 h-20 rounded-full widget-border-primary-200 border animate-[ping_2s_ease-out_infinite]" />
                        <div className="absolute w-16 h-16 rounded-full widget-border-primary-100 border-2" />
                        <div className="absolute w-12 h-12 rounded-full widget-bg-primary-50" />
                        <div className="relative w-10 h-10 rounded-full widget-bg-primary-500 flex items-center justify-center shadow-sm">
                            <CheckIcon className="w-5 h-5 text-white" />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <h2
                            className="text-xl font-semibold tracking-tight widget-text-primary-color-600"
                            data-testid={testId}
                        >
                            {title}
                        </h2>
                        <p
                            className="text-sm widget-text-primary-color-500 max-w-xs mx-auto"
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    </div>
                </div>

                {warning && (
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
                        <div className="flex items-start gap-2.5">
                            <div className="shrink-0 w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                                <ExclamationTriangleIcon className="w-3.5 h-3.5 text-amber-600" />
                            </div>
                            <p className="text-xs font-medium text-amber-800 leading-relaxed pt-1">{warning}</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

function resolveSuccessCopy({
    t,
    isClientAuthenticated,
    isConfirmed,
    hasDirectBookingResult
}: {
    t: (key: string) => string
    isClientAuthenticated: boolean
    isConfirmed: boolean
    hasDirectBookingResult: boolean
}): { title: string; description: string; warning: string | null; testId: string } {
    if (!isClientAuthenticated) {
        if (hasDirectBookingResult) {
            if (isConfirmed) {
                return {
                    title: t('widgets.booking.success.confirmedTitle'),
                    description: t('widgets.booking.success.confirmedText'),
                    warning: null,
                    testId: 'booking-success-confirmed'
                }
            }

            return {
                title: t('widgets.booking.success.pendingTherapistTitle'),
                description: t('widgets.booking.success.pendingTherapistText'),
                warning: null,
                testId: 'booking-success-pending-therapist'
            }
        }

        return {
            title: t('widgets.booking.success.title'),
            description: t('widgets.booking.success.text1'),
            warning: t('widgets.booking.success.text2'),
            testId: 'booking-success-pending'
        }
    }

    if (isConfirmed) {
        return {
            title: t('widgets.booking.success.confirmedTitle'),
            description: t('widgets.booking.success.confirmedText'),
            warning: null,
            testId: 'booking-success-confirmed'
        }
    }

    return {
        title: t('widgets.booking.success.pendingTherapistTitle'),
        description: t('widgets.booking.success.pendingTherapistText'),
        warning: null,
        testId: 'booking-success-pending-therapist'
    }
}
